<template>
  <div id="step_creation_modal">
    <CustomModal
      :title="`Add Step ${Number(targetPosition) ?? Number(stepsLength) + 1}`"
      v-model:visible="modalVisible"
      @update:visible="handleModalClose"
      :width="'32vw'"
    >
      <StepOptionsView
        v-if="!selectedStep"
        :step-options="processedStepOptions"
        :step-status="stepStatus"
        @select-step="selectStep"
      />

      <StepConfigurationView
        v-else
        :selected-step-detail="selectedStepDetail"
        :steps-length="stepsLength"
        :automation-type="automationType"
        :form-data="formStepData"
        :sequence-id="sequenceId"
        @update:form-data="updateFormData"
        @change-step="changeStep"
      />

      <template #footer>
        <a-button
          v-if="!selectedStep"
          class="secondary_medium"
          @click="$emit('update:visible', false)"
        >
          Cancel
        </a-button>

        <a-button
          v-else
          class="primary_medium"
          @click="createStep"
          :loading="isCreating"
        >
          Create Step
        </a-button>
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import CustomModal from "@/src/components/CustomModal.vue";
import SequenceService from "@/src/api/SequenceService.js";
import StepOptionsView from "./StepOptionsView.vue";
import StepConfigurationView from "./StepConfigurationView.vue";
import { sequenceStepTypes,sequenceAutomationTypes} from "@/src/constants/sequenceConstants.js";
import {
  stepOptions,
  generateStepId,
} from "@/src/constants/sequenceStepOptions";
import { getFeatureBlockStatus } from "@/src/helpers/stepOptionsHelper.js";

const props = defineProps({
  visible: Boolean,
  sequenceId: [String, Number],
  stepsLength: Number,
  stepStatus: {
    type: Object,
    required: true,
  },
  targetPosition: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(["update:visible", "step-created"]);
const router = useRouter();
const store = useStore();

const modalVisible = computed({
  get: () => props.visible,
  set: (value) => emit("update:visible", value),
});

const selectedStep = ref(false);
const stepType = ref("");
const automationType = ref("");
const selectedStepDetail = ref({});
const isCreating = ref(false);

const formStepData = ref({
  automation_type: "",
  step_type: "",
  task_priority: "MEDIUM",
  task_description: "",
  timing_type: "SCHEDULE",
  delay_time: 2,
  position: 0,
  instruction: "",
  pause_until_complete: true,
  delay_time_unit: "DAYS",
  metadata: null,
  assign_team_member: false,
  assigned_to_id: null,
  assign_to_owner: true
});

const updateFormData = (updatedData) => {
  formStepData.value = { ...updatedData };
};

onMounted(async () => {
  const featureBlockStatus = await getFeatureBlockStatus(store);
  for (const key in props.stepStatus) {
    if (featureBlockStatus[key]) {
      props.stepStatus[key] = featureBlockStatus[key];
    }
  }
});

const processedStepOptions = computed(() => {
  const processed = JSON.parse(JSON.stringify(stepOptions));

  for (const category in processed) {
    processed[category].options = processed[category]?.options?.map(
      (option) => ({
        ...option,
        id: generateStepId(option?.automationType, option?.stepType),
        action: () =>
          selectStep(
            option?.stepType,
            option?.automationType,
            generateStepId(option?.automationType, option?.stepType),
          ),
      }),
    );
  }
  return processed;
});

const selectStep = (step_type, automation_type, id) => {
  if (props.stepStatus[id]?.disabled) {
    return;
  }
  stepType.value = step_type;
  automationType.value = automation_type;
  formStepData.value.step_type = step_type;
  formStepData.value.automation_type = automation_type;
  formStepData.value.metadata = null;

  if (formStepData.value.step_type === "LINKEDIN_INVITE" && formStepData.value.automation_type !== "TASK") {
    formStepData.value.pause_until_complete = true
  }

  const allOptions = Object.values(processedStepOptions.value).flatMap(
    (category) => category?.options || [],
  );
  selectedStepDetail.value =
    allOptions.find((option) => option?.id === id) || {};
  selectedStep.value = true;
};

const createStep = async () => {
  if (isCreating.value) return;
  
  isCreating.value = true;
  
  try {
    formStepData.value.position = (props.targetPosition > 0) ? Number(props.targetPosition) : Number(props.stepsLength) + 1;

    const stepData = {
      ...formStepData.value,
      assigned_to_id: formStepData.value.assign_team_member ? formStepData.value.assigned_to_id : null,
      assign_to_owner: formStepData.value.automation_type === 'TASK' ? (formStepData.value.assign_to_owner ?? true) : false
    };
    
    if (stepData.assign_to_owner) {
      stepData.assigned_to_id = null;
    }
    
    if ('assign_team_member' in stepData) {
      delete stepData.assign_team_member;
    }

    const response = await SequenceService.createSequenceStep(
      props.sequenceId,
      stepData
    );
    if (!response?.success) {
      message.error("Failed to create step");
      return;
    }

    emit("step-created");
    
    if (
      automationType.value === sequenceAutomationTypes.TASK &&
      stepType.value !== sequenceStepTypes.EMAIL
    ) {
      changeStep();
      return;
    }
    await router.push(
      `/sequences/${props.sequenceId}/step/${response?.variant?.id}/edit`,
    );
  } catch (error) {
    console.error(`Failed to create step..`, error);
    message.error("Failed to create step");
  } finally {
    resetForm();
  }
};

const resetForm = () => {
  modalVisible.value = false;
  Object.assign(formStepData.value, {
    automation_type: "",
    step_type: "",
    task_priority: "MEDIUM",
    task_description: "",
    instruction: "",
    pause_until_complete: true,
    timing_type: "SCHEDULE",
    delay_time: 2,
    delay_time_unit: "DAYS",
    metadata: null,
    assign_team_member: false,
    assigned_to_id: null,
    assign_to_owner: true
  });
  isCreating.value = false;
};

const changeStep = () => {
  stepType.value = "";
  automationType.value = "";
  selectedStepDetail.value = {};
  selectedStep.value = false;
  formStepData.value.step_type = "";
  formStepData.value.automation_type = "";
  formStepData.value.instruction = "";
  formStepData.value.metadata = null;
};

const handleModalClose = (visible) => {
  modalVisible.value = visible;
  if (!visible) {
    selectedStep.value = false;
    resetForm();
  }
};

watch(
  () => formStepData.value.delay_time,
  (newVal) => {
    if (newVal < 0) {
      formStepData.value.delay_time = 0;
    }
  },
);
</script>

<style scoped>
.type_option,
.selected_option {
  background-color: var(--white-opacity-2);
  border: 1px solid var(--white-opacity-8);
}

.type_option:hover {
  border: 1px solid var(--white-opacity-60);
}
</style>
